import { useState } from 'react';
import { ExportMenu } from '../ExportMenu';
import { Pagination } from '../Pagination';
import StatusBadge from '../StatusBadge';
import { StatusTimeline } from '../StatusTimeline';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Building2, CreditCard, Eye, Printer, Trash2, Undo2, Pencil, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { bankChip, bankLabel } from '../../utils/requestTheme';

const DP_BANKS = [
  { value: 'BSI', label: 'BSI', btn: 'bg-emerald-600 hover:bg-emerald-700', text: 'text-emerald-600' },
  { value: 'BRI', label: 'BRI', btn: 'bg-blue-600 hover:bg-blue-700', text: 'text-blue-600' },
];

const rp = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

/**
 * Tabel Down Payment di Payment Requests — tombol aksinya disamakan dengan
 * tabel Internal/Operasional: Detail, Voucher, Edit Admin Bank, Edit
 * Keterangan, Revert, Hapus, plus tombol Proses BSI/BRI miliknya sendiri.
 * DP tidak punya potongan PPh (dihitung di invoice final), hanya admin bank.
 */
export function DownPaymentsTable({ appliedSearch, bankSelections, dpPage, dpTotal, dpTotalPages, fetchData, filteredDownPayments, getSelectedForBank, handleBankSelect, handleProcessToBank, openDeleteDialog, processingBank, setShowVoucherDialog, setVoucherKeterangan, setVoucherTarget, user }) {
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null); // { mode: 'admin'|'keterangan', dp, value }
  const [saving, setSaving] = useState(false);
  const canAct = ['approval', 'admin'].includes(user?.role);

  const saveEdit = async () => {
    if (!edit) return;
    setSaving(true);
    try {
      if (edit.mode === 'admin') {
        await api.patch(`/down-payments/${edit.dp.id}/edit-deductions`, { admin_bank: parseFloat(edit.value) || 0 });
        toast.success('Admin bank DP diperbarui');
      } else {
        await api.patch(`/down-payments/${edit.dp.id}/edit-details`, { keterangan: edit.value });
        toast.success('Keterangan DP diperbarui');
      }
      setEdit(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const revert = async (dp) => {
    try {
      const res = await api.patch(`/down-payments/${dp.id}/revert`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengembalikan DP');
    }
  };

  if (!canAct || filteredDownPayments.length === 0) return null;

  const adminPreview = edit?.mode === 'admin' ? parseFloat(edit.value) || 0 : 0;

  return (
    <>
      <Card className="border-l-4 border-l-amber-500" data-testid="dp-card">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-amber-600" />
                Down Payments
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">DP</span>
              </CardTitle>
              <p className="text-sm text-slate-500">Uang muka supplier dari Purchasing — dipotong penuh di invoice final</p>
            </div>
            <div className="flex gap-2">
              {DP_BANKS.map((b) => (
                <Button
                  key={b.value}
                  size="sm"
                  className={b.btn}
                  onClick={() => handleProcessToBank(b.value, 'dp')}
                  disabled={processingBank || getSelectedForBank(b.value, 'dp').length === 0}
                  data-testid={`process-dp-${b.value.toLowerCase()}-btn`}
                >
                  <Building2 className="h-4 w-4 mr-1" />
                  Proses {b.label} ({getSelectedForBank(b.value, 'dp').length})
                </Button>
              ))}
              <ExportMenu table="down-payments" params={{ search: appliedSearch }} />
              {/* Sengaja tanpa Hapus Semua/Import massal: hapus DP wajib lewat
                  cascade per baris (PO & invoice yang memotong DP ikut disesuaikan). */}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="dp-table">
              <thead className="bg-amber-50">
                <tr className="text-left">
                  <th className="px-3 py-3 text-sm font-semibold text-center">
                    <div className="flex flex-col items-center gap-1">
                      {DP_BANKS.map((b) => <span key={b.value} className={b.text}>{b.label}</span>)}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-sm font-semibold">No. DP</th>
                  <th className="px-3 py-3 text-sm font-semibold">No. PO</th>
                  <th className="px-3 py-3 text-sm font-semibold">Tanggal</th>
                  <th className="px-3 py-3 text-sm font-semibold">Supplier</th>
                  <th className="px-3 py-3 text-sm font-semibold">Perusahaan</th>
                  <th className="px-3 py-3 text-sm font-semibold">Nilai DP</th>
                  <th className="px-3 py-3 text-sm font-semibold">Admin Bank</th>
                  <th className="px-3 py-3 text-sm font-semibold">Dibayarkan</th>
                  <th className="px-3 py-3 text-sm font-semibold">Status</th>
                  <th className="px-3 py-3 text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDownPayments.map((dp) => {
                  const processed = dp.bank_selected || dp.status !== 'submitted';
                  const selected = bankSelections[`dp_${dp.id}`];
                  return (
                    <tr key={dp.id} className={`border-b ${processed ? 'bg-slate-100 opacity-70' : ''}`} data-testid={`dp-row-${dp.id}`}>
                      <td className="px-3 py-3">
                        {!processed ? (
                          <div className="flex flex-col items-center gap-2">
                            {DP_BANKS.map((b) => (
                              <label key={b.value} className="flex items-center gap-1 cursor-pointer">
                                <Checkbox
                                  checked={selected === b.value}
                                  onCheckedChange={() => handleBankSelect(dp.id, 'dp', b.value)}
                                  className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                  data-testid={`dp-${b.value.toLowerCase()}-radio-${dp.id}`}
                                />
                                <span className={`text-xs font-semibold ${b.text}`}>{b.label}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${dp.bank_selected ? bankChip(dp.bank_selected) : 'text-slate-400'}`}>
                            {dp.bank_selected ? bankLabel(dp.bank_selected) : '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 font-mono text-sm font-semibold">{dp.dp_number}</td>
                      <td className="px-3 py-3 font-mono text-sm">{dp.po_number}</td>
                      <td className="px-3 py-3 text-xs text-slate-600">{dp.created_at ? new Date(dp.created_at).toLocaleDateString('id-ID') : '-'}</td>
                      <td className="px-3 py-3 text-sm">{dp.supplier_name}</td>
                      <td className="px-3 py-3 text-sm">{dp.company_name}</td>
                      <td className="px-3 py-3 font-mono text-sm font-bold text-amber-700">{rp(dp.amount)}</td>
                      <td className="px-3 py-3 font-mono text-sm">{dp.admin_bank ? rp(dp.admin_bank) : '-'}</td>
                      <td className="px-3 py-3 font-mono text-sm">{rp((dp.amount || 0) - (dp.admin_bank || 0))}</td>
                      <td className="px-3 py-3"><StatusBadge status={dp.status} /></td>
                      <td className="px-3 py-3">
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => setDetail(dp)} data-testid={`view-dp-${dp.id}`} title="Lihat Detail">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            className="border-purple-400 text-purple-700 hover:bg-purple-50"
                            onClick={() => { setVoucherTarget({ type: 'dp', data: dp }); setVoucherKeterangan(''); setShowVoucherDialog(true); }}
                            data-testid={`print-voucher-dp-${dp.id}`}
                            title="Print Voucher Pembayaran"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          {dp.status === 'submitted' && !dp.bank_selected && (
                            <Button
                              size="sm" variant="outline"
                              className="border-blue-400 text-blue-700 hover:bg-blue-50"
                              onClick={() => setEdit({ mode: 'admin', dp, value: String(dp.admin_bank || 0) })}
                              data-testid={`edit-deductions-dp-${dp.id}`}
                              title="Edit Admin Bank"
                            >
                              <Calculator className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline"
                            className="border-amber-400 text-amber-700 hover:bg-amber-50"
                            onClick={() => setEdit({ mode: 'keterangan', dp, value: dp.keterangan || '' })}
                            data-testid={`edit-details-dp-${dp.id}`}
                            title="Edit Keterangan"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {dp.status === 'bank_process' && (
                            <Button
                              size="sm" variant="outline"
                              className="border-amber-400 text-amber-700 hover:bg-amber-50"
                              onClick={() => revert(dp)}
                              data-testid={`revert-dp-${dp.id}`}
                              title="Kembalikan ke Menunggu Pilih Bank"
                            >
                              <Undo2 className="h-4 w-4" />
                            </Button>
                          )}
                          {user?.role === 'admin' && (
                            <Button size="sm" variant="destructive" onClick={() => openDeleteDialog(dp, 'down_payment')} data-testid={`delete-dp-${dp.id}`} title="Hapus DP">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={dpPage} totalPages={dpTotalPages} total={dpTotal} onPageChange={(p) => fetchData(undefined, undefined, p)} />
        </CardContent>
      </Card>

      {/* Detail DP */}
      <Dialog open={Boolean(detail)} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">DP</span>
              Detail Down Payment
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4" data-testid="dp-detail">
              <StatusTimeline docType="down_payment" docId={detail.id} fallbackDates={[{ label: 'Dibuat', at: detail.created_at, by: detail.created_by_name }, { label: 'Terbayar', at: detail.paid_at }]} />
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-slate-500">No. DP</p><p className="font-mono font-semibold">{detail.dp_number}</p></div>
                <div><p className="text-slate-500">Status</p><StatusBadge status={detail.status} /></div>
                <div><p className="text-slate-500">No. PO</p><p className="font-mono">{detail.po_number}</p></div>
                <div><p className="text-slate-500">Total PO</p><p className="font-mono">{rp(detail.total_po)}</p></div>
                <div><p className="text-slate-500">Supplier</p><p className="font-semibold">{detail.supplier_name}</p></div>
                <div><p className="text-slate-500">Perusahaan</p><p className="font-semibold">{detail.company_name}</p></div>
                <div><p className="text-slate-500">Nilai DP</p><p className="font-mono font-semibold">{rp(detail.amount)}</p></div>
                <div><p className="text-slate-500">Admin Bank</p><p className="font-mono">{rp(detail.admin_bank)}</p></div>
                <div><p className="text-slate-500">Nilai Dibayarkan</p><p className="font-mono font-bold text-emerald-700">{rp((detail.amount || 0) - (detail.admin_bank || 0))}</p></div>
                <div><p className="text-slate-500">Bank</p><p>{detail.bank_selected ? bankLabel(detail.bank_selected) : '-'}</p></div>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 text-sm grid grid-cols-2 gap-3">
                <p className="col-span-2 font-semibold text-amber-800">Rekening Tujuan</p>
                <div><p className="text-slate-500">Bank</p><p className="font-semibold">{detail.supplier_data?.nama_bank || '-'}</p></div>
                <div><p className="text-slate-500">No. Rekening</p><p className="font-mono font-semibold">{detail.supplier_data?.nomor_rekening || '-'}</p></div>
              </div>
              {detail.keterangan && <p className="text-sm"><span className="text-slate-500">Keterangan:</span> {detail.keterangan}</p>}
              <p className="text-xs text-slate-500">Dibuat oleh {detail.created_by_name || '-'}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Admin Bank / Keterangan */}
      <Dialog open={Boolean(edit)} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{edit?.mode === 'admin' ? 'Edit Admin Bank DP' : 'Edit Keterangan DP'}</DialogTitle>
          </DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="font-mono font-semibold">{edit.dp.dp_number}</p>
                <p className="text-slate-500">{edit.dp.supplier_name} — Nilai DP {rp(edit.dp.amount)}</p>
              </div>
              {edit.mode === 'admin' ? (
                <div className="space-y-2">
                  <Label>Admin Bank (Rp)</Label>
                  <Input type="number" min="0" value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} data-testid="dp-admin-bank-input" />
                  <p className="text-xs text-slate-500">DP tidak dipotong PPh — PPh dihitung penuh di invoice final.</p>
                  <p className="text-sm">Nilai dibayarkan: <strong className="font-mono">{rp((edit.dp.amount || 0) - adminPreview)}</strong></p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Keterangan</Label>
                  <textarea
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                    value={edit.value}
                    onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                    data-testid="dp-keterangan-input"
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEdit(null)}>Batal</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" onClick={saveEdit} disabled={saving} data-testid="dp-edit-save">
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
